/** Core domain types for the Lantern taste-profiling engine. */

export const AXIS_KEYS = ["terrain", "era", "fame", "energy", "lens"] as const;
export type AxisKey = (typeof AXIS_KEYS)[number];

export const UNI_KEYS = ["cul", "adv", "cra", "nig", "sho"] as const;
export type UniKey = (typeof UNI_KEYS)[number];

/** Position on each taste axis, -1..1. */
export type AxisVector = Record<AxisKey, number>;
/** Interest weights, 0..1. */
export type UniVector = Record<UniKey, number>;

/** A single catalog entry — one swipeable experience. */
export interface Experience {
  id: string;
  en: string;
  cn: string;
  city: string;
  region: string;
  days: number;
  /** Comma-separated month ranges, e.g. "3-6,9-11", or "全年" for year-round. */
  months: string;
  /** Child-friendliness, 0..1. */
  family: number;
  /** Relative cost band, 1..3. */
  cost: number;
  kw: string;
  hook: string;
  ax: AxisVector;
  u: UniVector;
  type?: undefined;
}

export type MbtiLetter = "E" | "I" | "N" | "S" | "F" | "T" | "J" | "P";

/** Raw swipe evidence per MBTI letter. */
export type Tally = Record<MbtiLetter, number>;

/** Normalised 0..1 share on each MBTI dimension (E, N, F, J poles). */
export interface Persona {
  E: number;
  N: number;
  F: number;
  J: number;
}

/** A personality statement card, shuffled into the experience deck. */
export interface PersonaCard {
  id: string;
  type: "persona";
  letter: MbtiLetter;
  en: string;
  media: string;
  g: [string, string, string];
  em: string;
  ax: AxisVector;
  u: UniVector;
}

export type Card = Experience | PersonaCard;

export const isPersonaCard = (c: Card): c is PersonaCard => (c as PersonaCard).type === "persona";

/** Derived lean (-1..1, which pole) and care (0..1, how much it matters). */
export interface AxisScore {
  lean: number;
  care: number;
}
export type Axes = Record<AxisKey, AxisScore>;
export type Uni = UniVector;

export interface SidekickTrust {
  verified: boolean;
  langs: string;
  trips: number;
  review: string;
}
export interface SidekickPrompt {
  q: string;
  a: string;
}
export interface Sidekick {
  name: string;
  /** Home city, matched against ACTIVE_CITIES. */
  city: string;
  base: string;
  abroad: string;
  line: string;
  emoji: string;
  persona: Persona;
  ax: AxisVector;
  u: UniVector;
  family: number;
  adds: Record<string, string>;
  fallbackAdds: string;
  prompts: SidekickPrompt[];
  trust: SidekickTrust;
  voice: string;
}

/** A sidekick with match scores attached, produced by buildProfile. */
export interface ScoredSidekick extends Sidekick {
  type: string;
  pFit: number;
  match: number;
}

export interface Route {
  name: string;
  cn: string;
  cities: string[];
}
export type RouteKey =
  "food" | "adventure" | "family" | "hidden" | "contrast" | "landscape" | "metro" | "classic";

export interface Archetype {
  /** Feature keys summed to score this archetype. */
  k?: string[];
  name: string;
  desc: string;
}

/** Transport mode plus optional duration in hours. */
export type Leg = [mode: string, hours: number | null];

export type SwipeDirection = "left" | "right";

/** One recorded swipe, including how long the card was on screen. */
export interface SwipeEntry {
  card: Card;
  dir: SwipeDirection;
  ms: number;
}

export interface CityPlan {
  city: string;
  exps: ScoredExperience[];
}
export interface ScoredExperience extends Experience {
  s: number;
}

export interface MapPoint {
  city: string;
  xy: number[];
  emoji: string;
  d: number;
}

/** The full computed read on a user, produced by buildProfile. */
export interface Profile {
  travelType: string;
  arch: Archetype;
  contrastSeeker: boolean;
  /** A single-city theme. Typed as CityTheme, not Route, since the pivot. */
  route: import("./themes.js").CityTheme;
  cityPlan: CityPlan[];
  per: number;
  zoneNote: boolean;
  sidekick: ScoredSidekick;
  shared: number;
  quips: string[];
  reasons: string[];
  mapPts: MapPoint[];
  signals: number;
  matchPct: number;
  /** Stops removed by party constraints, with reasons. Surfaced, not hidden. */
  excluded: import("./suitability.js").Exclusion[];
}

/** A planner cell: an experience, a sidekick-only teaser, or empty. */
export type PlannerCell = ScoredExperience | { locked: true } | null;
export const isLocked = (c: PlannerCell): c is { locked: true } => c !== null && "locked" in c;

export interface CityItinerary {
  city: string;
  grid: PlannerCell[][];
}

// ---------------------------------------------------------------- guides --

/** How you actually get around at a site — the usual expectation gap. */
export type AccessMode =
  | "flat" // level ground, minimal walking
  | "walk" // walking is the point
  | "stairs" // significant stair climbing
  | "cable" // cable car / lift does the work
  | "boat"
  | "drive"
  | "train";

/**
 * Editorial reality-check for one experience.
 *
 * These are verifiable facts (altitude, stair counts, how you ascend, season
 * windows), NOT scraped or invented traveller reviews. Anything uncertain is
 * left out rather than guessed — an empty field is honest, a wrong altitude
 * is dangerous.
 */
export interface Guide {
  /**
   * Metres above sea level **where visitors actually spend their time**, not
   * the highest point on the map. The guardrails key off this, so using a
   * summit figure for a valley people walk along would exclude stops that are
   * perfectly fine. Record the reachable maximum in altitudeMax instead.
   */
  altitude?: number;
  /** Highest point a visitor can reach, when meaningfully above `altitude`. */
  altitudeMax?: number;
  /** 1 = flat stroll, 3 = a few hours on your feet, 5 = hard or exposed. */
  effort: 1 | 2 | 3 | 4 | 5;
  access: AccessMode;
  /** Youngest age this is reasonable for. Omitted when there is no concern. */
  minAge?: number;
  /** What the photos do not tell you. Omitted when there is no real gap. */
  realityEn?: string;
  realityCn?: string;
  /** Concrete practical notes. */
  tipsEn?: string[];
  tipsCn?: string[];
  /** Crowding or season warning. */
  seasonEn?: string;
  seasonCn?: string;
}
